import store from '@/state/store';
import projectSlice from '@/state/project.slice';
import { CRUDEventType, ProjectEventType, ProjectStatus, UserStatus } from '@bimdata/definitions/const';
import { Event } from '@bimdata/definitions/dto';
import invite from './invite';
import join from './join';
import exclude from './exclude';
import changeAccess from './change-access';
import { Notification } from '@/state/types/notification';
import { accessLevelLabels } from '@/contracts';
import changeBlockingStatus from './change-blocking-status';
import { get as getProject } from '@/api/project';
import { get as getUser } from '@/api/user';
import t from '@/utils/translate';
import { invitations as invitationList } from '@/loaders/user';
import { ApiError } from '@bimdata/client';

const enrichProject = async (projectCode: string): Promise<{ name?: string, deleted: boolean }> => {
  let project = null;
  let deleted = false;
  try {
    project = await getProject(projectCode);
    deleted = project.status === ProjectStatus.ARCH;
  } catch (e) {
    if (
      !(e instanceof ApiError) ||
      (e.code !== 403 && e.code !== 404)
    ) {
      throw e;
    }
    deleted = (e.code === 404);
  }
  return { name: project?.name, deleted };
}

const enrichUser = async (authorId: string): Promise<{ label: string, deleted: boolean }> => {
  const user = await getUser(authorId);
  return {
    label: user ? (user.fullName || user.id) : t('user.deleted'),
    deleted: user ? user.status !== UserStatus.ACTIVE : false
  };
}

const getNotification = async (event: Event): Promise<Notification | null> => {
  const state = store.getState();
  const profile = state.authentication.profile;
  if (!profile) {
    return null;
  }

  switch (event.type) {
    case ProjectEventType.INVITE: {
      const { participantId, invitationId } = event.data;
      if (participantId === profile.id) {
        if (!state.authentication.invitations) {
          await invitationList();
        }
        const invitation = state.authentication.invitations?.find((inv) => (
          inv.id === invitationId
        ));
        if (!invitation) {
          return null;
        }
        return {
          eventId: event.id,
          date: event.date,
          title: t('project.event.invitation.title'),
          message: t(
            'project.event.invitation.message',
            {
              user: invitation.inviter.fullName,
              project: invitation.project.name,
              formatParams: {
                user: { deleted: invitation.inviter.status !== UserStatus.ACTIVE },
                project: { deleted: invitation.project.status === ProjectStatus.ARCH }
              },
            },
            true
          ),
          delivered: event.delivered,
          link: '/project',
          event
        };
      }
    } break;
    case ProjectEventType.EXCLUDE: {
      const { projectCode, projectName, userId, invitationId, authorId } = event.data;
      if (userId === profile.id) {
        if (invitationId) {
          /*
          return {
            eventId: event.id,
            date: event.date,
            title: t('project.event.invitationCanceled.title'),
            message: t(
              'project.event.invitationCanceled.message',
              {
                user: author?.fullName || authorId,
                project: project?.name || projectName || projectCode,
                formatParams: {
                  user: { deleted: invitation.inviter.status !== UserStatus.ACTIVE },
                  project: { deleted: invitation.project.status === ProjectStatus.ARCH }
                },
              },
              true
            ),
            delivered: event.delivered,
            event
          };
          */
        } else {
          const u = await enrichUser(authorId);
          return {
            eventId: event.id,
            date: event.date,
            title: t('project.event.exclude.title'),
            message: t(
              'project.event.exclude.message',
              {
                user: u.label,
                project: projectName || projectCode,
                formatParams: {
                  user: { deleted: u.deleted }
                },
              },
              true
            ),
            delivered: event.delivered,
            event
          };
        }
      }
    } break;
    case ProjectEventType.CHANGE_ACCESS: {
      const { projectCode, userId, invitationId, access, prevAccess, authorId } = event.data;
      if (userId === profile.id) {
        if (!invitationId) {
          const u = await enrichUser(authorId);
          const p = await enrichProject(projectCode);
          return {
            eventId: event.id,
            date: event.date,
            title: t('project.event.access.title'),
            message: t(
              'project.event.access.message',
              {
                user:  u.label,
                project: p.name || projectCode,
                formatParams: {
                  user: { deleted: u.deleted },
                  project: { deleted: p.deleted }
                },
                access: accessLevelLabels(access),
                previous: accessLevelLabels(prevAccess),
              },
              true
            ),
            delivered: event.delivered,
            event
          };
        }
      }
    } break;
    case ProjectEventType.EXIT: {
      const { projectCode, userId, invitationId, inviterId } = event.data;
      if (invitationId && inviterId === profile.id) {
        const u = await enrichUser(userId);
        let project = null;
        let deleted = false;
        try {
          project = await getProject(projectCode);
          deleted = project.status === ProjectStatus.ARCH;
        } catch (e) {
          if (e.code === 403) {
            //Инвайтер уже не имеет доступа к проекту, поэтому его не нужно уведомлять
            return null;
          }
          if (e.code === 404) {
            deleted = true;
          } else {
            throw e;
          }
        }

        return {
          eventId: event.id,
          date: event.date,
          title: t('project.event.invitationDeclined.title'),
          message: t(
            'project.event.invitationDeclined.message',
            {
              user: u.label,
              project: project?.name || projectCode,
              formatParams: {
                user: { deleted: u.deleted },
                project: { deleted }
              },
            },
            true
          ),
          delivered: event.delivered,
          event
        };
      }
    } break;
    case ProjectEventType.CHANGE_BLOCK: {
      const { projectCode, projectName, userId, blocked, authorId } = event.data;
      if (userId === profile.id) {
        const u = await enrichUser(authorId);
        if (blocked) {
          return {
            eventId: event.id,
            date: event.date,
            title: t('project.event.block.title'),
            message: t(
              'project.event.block.message',
              {
                user: u.label,
                project: projectName || projectCode,
                formatParams: {
                  user: { deleted: u.deleted }
                },
              },
              true
            ),
            delivered: event.delivered,
            event
          };
        } else {
          const p = await enrichProject(projectCode);
          return {
            eventId: event.id,
            date: event.date,
            title: t('project.event.unblock.title'),
            message: t(
              'project.event.unblock.message',
              {
                user: u.label,
                project: p.name || projectName || projectCode,
                formatParams: {
                  user: { deleted: u.deleted },
                  project: { deleted: p.deleted }
                },
              },
              true
            ),
            delivered: event.delivered,
            event
          };
        }
      }
    } break;
  }

  return null;
};

const apply = async (event: Event) => {
  const state = store.getState();
  const profile = state.authentication.profile;
  if (!profile) {
    return;
  }

  switch (event.type) {
    case CRUDEventType.CREATE: {
      let project = null;
      try {
        project = await getProject(event.data.projectCode);
      } catch (error) {
        if (error.code !== 404) {
          throw error;
        }
      }
      if (project) {
        store.dispatch(projectSlice.actions.created(project));
      }
    } break;
    case CRUDEventType.UPDATE: {
      let project = null;
      try {
        project = await getProject(event.data.projectCode);
      } catch (error) {
        if (error.code !== 404) {
          throw error;
        }
      }
      if (project) {
        store.dispatch(
          projectSlice.actions.update({
            code: event.data.projectCode,
            data: project,
          })
        );
      }
    } break;
    case CRUDEventType.DELETE:
      store.dispatch(projectSlice.actions.removeProject(event.data.projectCode));
      break;
    case ProjectEventType.INVITE:
      await invite(event, profile);
      break;
    case ProjectEventType.JOIN:
      await join(event, profile);
      break;
    case ProjectEventType.EXCLUDE:
    case ProjectEventType.EXIT:
      await exclude(event, profile);
      break;
    case ProjectEventType.CHANGE_ACCESS:
      await changeAccess(event, profile, state.projects.current);
      break;
    case ProjectEventType.CHANGE_BLOCK:
      await changeBlockingStatus(event, profile);
      break;

  }
}

export { apply, getNotification };